/**
 * Textos (`ca`) del Recompte d'existències del BackOffice (OBR-147): la llista, el diàleg de creació, la pantalla del recompte, la
 * validació, la cancel·lació, els lots trobats i els errors de les RPC. Namespace `backoffice.stockCounts`. Tall propi: no es toca
 * `ca.ts`. Les tres llengües han de tenir les mateixes claus (i18n.test.ts); cap text literal a les vistes.
 *
 * Vocabulari: «Recompte d'existències» (mai «inventari»); «Existències» (mai «Stock»); «teòrica» = el que diu el llibre;
 * «comptada» = el que s'ha trobat; «sobra» / «falta» = la diferència; «ajust» = el moviment amb motiu «Recompte».
 */

export const countCa = {
  'backoffice.stockCounts': {
    // La llista
    title: 'Recomptes d’existències',
    subtitle:
      'Compta les existències reals, compara-les amb les teòriques i ajusta les diferències.',
    create: 'Nou recompte',
    searchLabel: 'Cerca',
    filterStatus: 'Estat',
    filterScope: 'Abast',
    filterFrom: 'Des del',
    filterTo: 'Fins al',
    allStatuses: 'Tots els estats',
    allScopes: 'Tots els abasts',
    status_open: 'Obert',
    status_validated: 'Validat',
    status_cancelled: 'Cancel·lat',
    scope_tenant: 'Tot l’obrador',
    scope_zone: 'Zona',
    scope_section: 'Secció',
    scope_location: 'Ubicació',
    scope_article: 'Article',
    scopeOf_tenant: 'Tot l’obrador',
    scopeOf_zone: 'Zona {{name}}',
    scopeOf_section: 'Secció {{name}}',
    scopeOf_location: 'Ubicació {{name}}',
    scopeOf_article: 'Article {{name}}',
    scopeUnknown: '—',
    colNumber: 'Número',
    colScope: 'Abast',
    colStatus: 'Estat',
    colProgress: 'Línies',
    colDate: 'Data',
    colBy: 'Creat per',
    progressOpen: '{{counted}} de {{total}} comptades',
    progressValidated: '{{adjusted}} ajustades de {{total}}',
    progressCancelled: '{{total}} línies sense ajustar',
    empty: 'Encara no hi ha cap recompte. Crea’n un per comptar les existències reals.',
    emptyFiltered: 'Cap recompte coincideix amb els filtres.',
    loadError: 'No s’han pogut carregar els recomptes. Torna-ho a provar.',
    listLimit: 'Es mostren els {{count}} recomptes més recents.',
    unknownPerson: '—',

    // El diàleg de creació
    createTitle: 'Nou recompte d’existències',
    createBody:
      'Es desa una fotografia de les existències teòriques de l’abast triat, lot a lot i lloc a lloc. Les existències no es bloquegen mentre comptes: els moviments continuen.',
    createScopeLabel: 'Què vols comptar?',
    createZoneLabel: 'Zona',
    createSectionLabel: 'Secció',
    createLocationLabel: 'Ubicació',
    createArticleLabel: 'Article',
    createTargetPlaceholder: 'Tria’n un',
    createNoTargets: 'Encara no hi ha res d’aquest tipus a l’estructura de l’obrador.',
    createNoteLabel: 'Nota (opcional)',
    createNotePlaceholder: 'Per exemple: recompte de tancament de mes',
    createConfirm: 'Crea el recompte',

    // La pantalla del recompte
    back: 'Tots els recomptes',
    notFound: 'Aquest recompte no existeix o no el pots veure.',
    detailLoadError: 'No s’ha pogut carregar el recompte. Torna-ho a provar.',
    linesLoadError: 'No s’han pogut carregar les línies del recompte. Torna-ho a provar.',
    createdBy: 'Creat per {{name}} el {{date}}',
    validatedBy: 'Validat per {{name}} el {{date}}',
    cancelledBy: 'Cancel·lat per {{name}} el {{date}}',
    cancelReasonLine: 'Motiu: {{reason}}',
    noteLine: 'Nota: {{note}}',
    validate: 'Valida el recompte',
    cancel: 'Cancel·la el recompte',
    addFound: 'Afegeix un lot trobat',
    filterAll: 'Totes',
    filter_pending: 'Pendents',
    filter_match: 'Coincideix',
    filter_short: 'Falta',
    filter_over: 'Sobra',
    filterAria: 'Filtra les línies pel resultat del recompte',
    state_pending: 'Sense comptar',
    state_match: 'Coincideix',
    state_short: 'Falta',
    state_over: 'Sobra',
    stateShortQuantity: 'Falta {{quantity}} {{unit}}',
    stateOverQuantity: 'Sobra {{quantity}} {{unit}}',
    colLot: 'Lot',
    colPlace: 'Lloc',
    colTheoretical: 'Teòrica',
    colNow: 'Ara',
    colAtValidation: 'En validar',
    colCounted: 'Comptada',
    colDifference: 'Diferència',
    colAdjustment: 'Ajust',
    noLot: 'Sense lot',
    noPlace: 'Sense lloc',
    expiresOn: 'Caduca el {{date}}',
    noExpiry: 'Sense caducitat',
    newLotTag: 'Lot nou',
    foundTag: 'Trobat',
    changedTag: 'Ha canviat',
    changedHint:
      'Ara n’hi ha {{quantity}} {{unit}}: algú ha mogut existències d’aquest lot des que es va crear el recompte.',
    currentLoading: 'Llegint les existències d’ara…',
    currentError:
      'No s’han pogut llegir les existències d’ara. El recompte es pot continuar, però no s’avisarà si han canviat.',
    confirmTheoretical: 'Confirma teòrica',
    confirmGroup: 'Confirma teòrica del grup',
    confirmNegative: 'Un saldo negatiu no es pot confirmar: compta’l de debò.',
    countedAria: 'Quantitat comptada de {{article}}, lot {{lot}}',
    countedInvalid: 'Escriu una quantitat vàlida (0 o més).',
    saving: 'Desant…',
    saved: 'Tot desat',
    saveError: 'No s’ha pogut desar el que has escrit. Torna-ho a provar.',
    saveRetry: 'Torna-ho a provar',
    groupLines: '{{count}} línies',
    warnChanged:
      'La teòrica ha canviat en {{count}} línies des que es va crear el recompte. En validar, l’ajust es calcula sobre les existències d’ara, no sobre la fotografia.',
    warnPending: 'Hi ha {{count}} línies sense comptar: no es tocaran en validar.',
    validatedBanner:
      'Recompte validat: {{adjusted}} ajustos, {{matched}} línies coincideixen i {{lots}} lots nous.',
    readOnlyValidated: 'Aquest recompte és validat i ja no es pot editar.',
    readOnlyCancelled: 'Aquest recompte està cancel·lat i no ha ajustat res.',
    emptyLines:
      'Aquest abast no tenia existències a la fotografia. Pots afegir-hi els lots que trobis.',
    noLinesFiltered: 'Cap línia coincideix amb aquest filtre.',
    adjustmentNone: 'Sense ajust',
    adjustmentNotCounted: 'No comptada',
    viewMovements: 'Mostra els moviments',

    // La validació
    validateTitle: 'Valida el recompte',
    validateBody:
      'Es generarà un moviment d’ajust amb el motiu «Recompte» per cada línia amb diferència. Un cop validat, el recompte ja no es pot editar.',
    validateSummaryAdjust:
      '{{count}} ajustos: {{increases}} sobren, {{decreases}} falten i {{lots}} lots nous.',
    validateNothing: 'No hi ha cap diferència: no es generarà cap moviment.',
    validateMatched: '{{count}} línies coincideixen i no generen cap moviment.',
    validateNotCounted: '{{count}} línies sense comptar: no es tocaran.',
    validateChanged:
      '{{count}} línies amb la teòrica canviada: l’ajust es calcula sobre les existències d’ara.',
    validateBlocked: 'Aquestes línies no es poden ajustar tal com estan:',
    validateBlockedHint: 'Corregeix-les abans de validar.',
    validateAdjustmentsTitle: 'Ajustos que es crearan',
    validateAdjustLine: '{{article}} · {{lot}} · {{place}}: {{from}} → {{to}} ({{delta}} {{unit}})',
    validateNewLotLine: '{{article}} · lot nou · {{place}}: {{to}} {{unit}}',
    validateMore: 'i {{count}} més',
    validateConfirm: 'Valida i ajusta',
    validateCurrentLoading: 'Llegint les existències d’ara…',
    validateCurrentError:
      'No s’han pogut llegir les existències d’ara. Pots validar igualment: el servidor ho comprova.',
    validateFailedLine: 'Línia {{article}} · {{lot}} · {{place}}: {{message}}',

    // La cancel·lació
    cancelTitle: 'Cancel·la el recompte',
    cancelBody: 'El recompte queda cancel·lat i no s’ajusta res. Cal escriure’n el motiu.',
    cancelLabel: 'Motiu de la cancel·lació',
    cancelPlaceholder: 'Per exemple: ens hem equivocat d’abast',
    cancelConfirm: 'Cancel·la el recompte',

    // Afegir un lot trobat
    foundTitle: 'Afegeix un lot trobat',
    foundBody:
      'Un lot que has trobat i no era a la fotografia. Si ja existeix al sistema, tria’l; si no, es crearà com a lot nou d’ajust quan validis.',
    foundArticle: 'Article',
    foundArticlePlaceholder: 'Cerca un article',
    foundMode: 'Tipus de lot',
    foundModeExisting: 'Un lot que ja existeix',
    foundModeNew: 'Un lot nou',
    foundLot: 'Lot',
    foundLotPlaceholder: 'Tria un lot',
    foundLotsLoading: 'Llegint els lots…',
    foundLotsError: 'No s’han pogut llegir els lots d’aquest article.',
    foundNoLots: 'Aquest article encara no té lots: tria «Un lot nou».',
    foundCode: 'Codi de lot del proveïdor (opcional)',
    foundExpiry: 'Caducitat',
    foundExpiryRequired: 'Obligatòria per a aquest article.',
    foundExpiryOptional: 'Opcional.',
    foundExpiryInvalid: 'Escriu una data vàlida.',
    foundPlace: 'Lloc',
    foundPlaceHint: 'Ha de ser dins de l’abast del recompte.',
    foundPlaceRequired: 'Tria un lloc.',
    foundQuantity: 'Quantitat comptada ({{unit}})',
    foundQuantityBare: 'Quantitat comptada',
    foundQuantityInvalid: 'Escriu una quantitat vàlida.',
    foundNote: 'Nota (opcional)',
    foundConfirm: 'Afegeix',
    foundProduction:
      'Aquest article només es produeix: el seu lot el crea una ordre de treball. Tria un lot que ja existeixi.',
    foundNoTracked: 'Aquest article no porta control d’existències.',

    // 2a passada: confirmar amb el saldo d’ara, caducitat d’un lot existent, lot creat, validació amb tot desat
    confirmCurrent: 'Confirma el d’ara',
    confirmCurrentHint:
      'Ha canviat des de la fotografia: es confirmen les {{quantity}} {{unit}} d’ara, no les de la foto.',
    confirmGroupCurrentHint:
      '{{count}} línies han canviat des de la fotografia: se’n confirma el saldo d’ara, no el de la foto.',
    lotExpiryLabel: 'Caducitat del lot',
    lotExpiryHint:
      'Aquest lot no en té i el recompte hi afegeix existències: indica-la per poder validar.',
    lotExpiryAria: 'Caducitat del lot {{lot}} de {{article}}',
    lotCreatedTag: 'Lot creat',
    validatePreparing: 'Desant les quantitats i llegint l’estat actual del recompte…',
    validateUnsaved:
      'Hi ha quantitats que no s’han pogut desar: sense això, el resum no seria el que es validarà.',
    validateRetry: 'Torna-ho a provar',
    leaveTitle: 'Hi ha quantitats sense desar',
    leaveBody: 'No s’han pogut desar les quantitats que has escrit. Si surts ara, es perdran.',
    leaveConfirm: 'Surt igualment',

    // Un lot trobat que ja és al recompte
    foundExistsTitle: 'Aquest lot ja és al recompte en aquest lloc',
    foundExistsCounted: 'Ja hi tens comptats {{quantity}} {{unit}}.',
    foundExistsPending: 'Encara no l’has comptat.',
    foundExistsChoice: 'Què fem amb el que ja hi havia comptat?',
    foundExistsAddShort: 'Suma-ho',
    foundExistsReplaceShort: 'Substitueix-ho',
    foundExistsAdd: 'Suma-ho: {{from}} + {{quantity}} = {{to}} {{unit}}',
    foundExistsReplace: 'Substitueix-ho: queden {{to}} {{unit}}',
    foundExpiryExistingRequired:
      'Aquest lot no té caducitat i l’article la demana: indica-la perquè es pugui ajustar.',
    foundLotsTruncated:
      'Només es mostren els {{limit}} lots més nous. Escriu el número del lot per cercar-lo.',
    foundLotSearch: 'Cerca un lot pel número',
    foundLotSearchPlaceholder: 'Per exemple: MP-L-00042',

    // Crear un recompte: l’estructura no s’ha pogut llegir
    createStructureError:
      'No s’ha pogut llegir l’estructura de l’obrador (zones, seccions i ubicacions): sense això no es pot triar un abast d’estructura.',
    createStructureRetry: 'Torna-ho a provar',

    // Errors de les RPC
    errForbidden: 'No tens permís per fer recomptes d’existències.',
    errBadScope: 'L’abast triat no és vàlid.',
    errTooLarge: 'L’abast té massa línies. Tria una zona, una secció, una ubicació o un article.',
    errClientUuidConflict:
      'Aquesta operació ja s’havia fet amb unes altres dades. Torna a obrir el diàleg i reintenta-ho.',
    errNotFound: 'Aquest recompte no existeix o no el pots veure.',
    errNotOpen: 'Aquest recompte ja no està obert: potser algú l’ha validat o cancel·lat.',
    errBadLine: 'Una de les línies no és vàlida.',
    errBadQuantity: 'La quantitat no és vàlida: ha de ser 0 o més.',
    errUnknownLine: 'Una de les línies ja no és d’aquest recompte. Recarrega la pantalla.',
    errUnknownArticle:
      'Aquest article no existeix, està de baixa o no porta control d’existències.',
    errUnknownLot: 'Aquest lot no és d’aquest article.',
    errBadPlace: 'Aquest lloc no és de l’obrador.',
    errOutOfScope: 'Aquest lloc és fora de l’abast del recompte.',
    errLotRequired: 'Cal indicar un lot (un que ja existeixi o un de nou).',
    errReasonRequired: 'Cal escriure el motiu.',
    errImmutable: 'Un recompte validat o cancel·lat no es pot modificar.',
    errGeneric: 'No s’ha pogut fer. Torna-ho a provar.',
  },
} as const;
